/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import IEnergyManagementProps, {
  IAny,
  IEMaaSContext,
  IElementForDisaggMeters,
  IRequestContext
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import {
  EnergyUsageScopeType,
  EnergyUsageUtilityType,
  IntervalType
} from '../../../shared/Utils/Constants';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import {
  getIntervalType,
  getIntervalTypeForEnergyDisaggregation
} from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import EmaasNILMEnergyDisaggregationDetailWidget from './EmaasNILMEnergyDisaggregationDetailWidget';
import EnergyConsumptionByEquipmentTypeComponent from './EnergyConsumptionByEquipmentType/EnergyConsumptionByEquipmentTypeComponent';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';

interface IElementsResponseDisagg {
  resources: IElementForDisaggMeters[];
}

const genNILMMeterDropdownOptions = (
  elements: IElementForDisaggMeters[],
  requestContext?: IRequestContext,
  emaasContext?: IEMaaSContext
): IOption[] => {
  const nilmMeters = elements.filter((e) => {
    return e.tags && e.tags.includes('NilmElectricMeter');
  });

  let preparedOptions: IOption[] = [];

  const hasDashboardVis = nilmMeters.filter((m) =>
    m.tags.includes('DashboardVisualization')
  );

  const checkForSiteINodeId = (m: IAny) => {
    return (
      m.spatialElementInfo &&
      (m.spatialElementInfo.id === requestContext?.context.nodeId ||
        m.spatialElementInfo.id === requestContext?.context.siteId)
    );
  };

  const isMainMeter = nilmMeters.filter(
    (m) =>
      checkForSiteINodeId(m) ||
      (m.suppliesSpatialElement &&
        (m.suppliesSpatialElement.includes(requestContext?.context.nodeId!) ||
          m.suppliesSpatialElement.includes(requestContext?.context.siteId!)))
  );

  if (hasDashboardVis.length > 0) {
    preparedOptions = [
      { text: hasDashboardVis[0].name, value: hasDashboardVis[0].id }
    ];
    preparedOptions = [
      ...preparedOptions,
      ...nilmMeters
        .filter((m) => m.id !== hasDashboardVis[0].id)
        .map((m) => ({ text: m.name, value: m.id }))
    ];
  } else if (isMainMeter.length > 0) {
    preparedOptions = [{ text: isMainMeter[0].name, value: isMainMeter[0].id }];
    preparedOptions = [
      ...preparedOptions,
      ...nilmMeters
        .filter((m) => m.id !== isMainMeter[0].id)
        .map((m) => ({ text: m.name, value: m.id }))
    ];
  } else {
    preparedOptions = [
      ...preparedOptions,
      ...nilmMeters.map((m) => ({ text: m.name, value: m.id }))
    ];
  }

  return preparedOptions;
};

const generateDisaggregationMeterDropdown = (
  elements: IElementForDisaggMeters[],
  requestContext?: IRequestContext,
  emaasContext?: IEMaaSContext
): IOption[] => {
  let preparedOptions: IOption[] = [];

  let siteMeters: IElementForDisaggMeters[] = [];
  let otherMeters: IElementForDisaggMeters[] = [];

  elements.forEach((e) => {
    let ca = e.customAttributes;
    if (ca) {
      const ca_map: { [k: string]: IAny } = {};
      ca.forEach((c) => {
        let parsed = c.value;
        try {
          parsed = JSON.parse(c.value);
        } catch (err) {}
        ca_map[c.key] = parsed;
      });
      if (ca_map['integrationDetails']) {
        let integrationDetails = ca_map['integrationDetails'];
        if (integrationDetails['IntegrationName'] === 'Disaggregation') {
          // conditions for redaptive
          const checkIntegration =
            emaasContext?.toggleRedaptiveIntegration &&
            integrationDetails['IntegrationType'] === 'Redaptive';

          // const checkSuppliesElement =
          //   e.suppliesElement && e.suppliesElement.length > 0;
          if (
            checkIntegration
            // &&
            // checkSuppliesElement
          ) {
            if (
              siteMeters.length === 0 &&
              integrationDetails['IntegrationMeterType'] === 'Site'
            ) {
              siteMeters.push(e);
            }
            // else if (integrationDetails['IntegrationMeterType'] === 'Meter') {
            //   otherMeters.push(e);
            // }
            else {
              // do nothing
            }
          }
        }
      }
    }
  });
  if (siteMeters.length > 0) {
    siteMeters.forEach((e) => {
      preparedOptions.push({ value: e.id, text: e.name });
    });
  }
  otherMeters.forEach((e) => {
    preparedOptions.push({ value: e.id, text: e.name });
  });

  return preparedOptions;
};

const getMeterDropdownOptions = (
  elements: IElementForDisaggMeters[],
  requestContext?: IRequestContext,
  emaasContext?: IEMaaSContext
): IOption[] => {
  let options: IOption[] = [
    ...genNILMMeterDropdownOptions(elements, requestContext, emaasContext),
    ...generateDisaggregationMeterDropdown(
      elements,
      requestContext,
      emaasContext
    )
  ];

  return options;
};

const EmaasEnergyDisaggregationDetails = (props: IEnergyManagementProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext, emaasContext } = props;
  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    emaasContext
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    emaasContext
  );

  const isMounted = useMounted();
  const [selectedMeter, setSelectedMeter] = useState<string | null>(null);

  // Old API
  // const connectorConfig = {
  //   name: 'emaas-energy-Disaggregation',
  //   options: {
  //     startDate: startDate,
  //     endDate: endDate,
  //     allDetails: 'true',
  //     utilityType: EnergyUsageScopeType.ELECTRIC,
  //     intervalType: getIntervalTypeForEnergyDisaggregation(
  //       requestContext.context.startTime as string,
  //       requestContext.context.endTime as string
  //     )
  //   }
  // };

  const dataByNilmMeterConnectorConfig = {
    name: 'emaas-energy-disaggregation-by-meter-id',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: EnergyUsageScopeType.ELECTRIC,
      intervalType: getIntervalTypeForEnergyDisaggregation(
        requestContext.context.startTime as string,
        requestContext.context.endTime as string
      )
    },
    routes: {
      meterId: selectedMeter!
    }
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig: dataByNilmMeterConnectorConfig,
    requestContext,
    bearerToken
  });

  const [isAllDataLoading, setAllDataLoading] = useState<boolean>(true);

  const UtilityBillType = EnergyUsageUtilityType.Electricity;

  // data connector def to fetch list of elements
  const [dataConnectorMeterList] = useConnectorAndDynamicProps({
    connectorConfig: { name: 'emaas-list-elements' },
    requestContext: {} as IRequestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      dataConnectorMeterList?.actions?.getAll();
    }
  }, []);

  const onMeterChange = (value: string) => {
    setSelectedMeter(value);
  };

  const loadData = async (reloadMeterList: boolean = false) => {
    if (reloadMeterList) {
      setAllDataLoading(true);
      await (dataConnectorMeterList &&
        dataConnectorMeterList.actions &&
        dataConnectorMeterList.actions.getAll());
      return;
    }
    await (dataConnector &&
      dataConnector.actions &&
      dataConnector.actions.getAll());
    setAllDataLoading(false);
  };

  useEffect(() => {
    if (isMounted && selectedMeter !== null) {
      loadData();
    }
  }, [requestContext, selectedMeter]);

  // Fetching list of elements
  const {
    all: meterListAll,
    loading: meterListLoading,
    error: meterListError,
    status: meterListStatus
  } = dataConnectorMeterList.data;
  const elementResponse = (meterListAll as unknown) as IElementsResponseDisagg;
  const isMeterListAvailable =
    elementResponse && elementResponse.resources ? true : false;

  const [meterDropdownOpts, setMeterDropdownOpts] = useState<IOption[]>([]);
  const [isLoadingMeterList, setIsLoadingMeterList] = useState<boolean>(
    meterListLoading
  );
  const [statusCodeMeterList, setStatusCodeMeterList] = useState<number | null>(
    0
  );
  const [errorInfoMeterList, setErrorInfoMeterList] = useState(meterListError);

  useEffect(() => {
    setIsLoadingMeterList(meterListLoading);
    if (
      (isMeterListAvailable || meterListError || meterListStatus) &&
      !meterListLoading
    ) {
      if (isMeterListAvailable) {
        const meterOptionList = getMeterDropdownOptions(
          elementResponse.resources,
          props.requestContext,
          props.emaasContext
        );
        setMeterDropdownOpts([...meterOptionList]);
        if (meterOptionList.length > 0) {
          onMeterChange(meterOptionList[0].value);
        } else {
          setAllDataLoading(false);
          setStatusCodeMeterList(204);
          return;
        }
      }
      setErrorInfoMeterList(meterListError);
      setStatusCodeMeterList(meterListStatus);
    }
  }, [isMeterListAvailable, meterListLoading, meterListStatus]);

  useEffect(() => {
    // Stop loading on NILMMeterList Error
    if (
      !isLoadingMeterList &&
      errorInfoMeterList &&
      errorInfoMeterList.rawResponse !== null
    ) {
      setAllDataLoading(false);
    }
  }, [errorInfoMeterList.rawResponse, isLoadingMeterList]);

  const onRefresh = async () => {
    await loadData(!isMeterListAvailable || !(meterDropdownOpts.length > 0));
    return;
  };

  return (
    <div className="all-utilities-details">
      <EmaasNILMEnergyDisaggregationDetailWidget
        dataConnector={dataConnector}
        headerTitle={t(d.ENERGYCONSUMPTIONNILM)}
        nilmTab={'NILMEnergyDisaggregation'}
        requestContext={requestContext}
        isMeterListLoading={isAllDataLoading}
        meterList={meterDropdownOpts}
        onRefresh={onRefresh}
        selectedMeter={selectedMeter}
        onMeterChange={onMeterChange}
        intervalType={getIntervalTypeForEnergyDisaggregation(
          requestContext.context.startTime as string,
          requestContext.context.endTime as string
        )}
        meterListStatusCode={statusCodeMeterList}
      />
      <EnergyConsumptionByEquipmentTypeComponent
        dataConnector={dataConnector}
        UtilityBillType={UtilityBillType}
        subscriptionType={requestContext.context.subscriptionType!}
        requestContext={requestContext}
        isMeterListLoading={isAllDataLoading}
        onRefresh={onRefresh}
        meterListStatusCode={statusCodeMeterList}
      />
    </div>
  );
};
export default EmaasEnergyDisaggregationDetails;
